/** Timeline effect sections. Mirrors autoclipper-backend src/lib/videoEffects.js
 *  (normalizeEffectSections) and the worker's fast-cut guard; the shared
 *  fixture videoEffectSectionsTestVectors.json pins identical behaviour. */
export const DYNAMIC_EFFECT_IDS = ['shake', 'zoom-punch', 'flash', 'glitch', 'rgb-split', 'focus-pull', 'strobe', 'vhs'] as const;
export const LOOK_EFFECT_IDS = ['look-warm', 'look-cool', 'look-cinema', 'look-vintage', 'look-vivid', 'look-mono', 'bw', 'grain', 'vignette', 'blur'] as const;
export type DynamicEffectId = typeof DYNAMIC_EFFECT_IDS[number];
export type LookEffectId = typeof LOOK_EFFECT_IDS[number];
export type EffectId = DynamicEffectId | LookEffectId;
export type EffectKnob = { key: 'speed' | 'frequency'; min: number; max: number; default: number };

export const EFFECT_SECTION_KNOBS: Partial<Record<EffectId, EffectKnob>> = {
  shake: { key: 'speed', min: 0.5, max: 3, default: 1 },
  'zoom-punch': { key: 'speed', min: 0.5, max: 4, default: 1.5 },
  flash: { key: 'frequency', min: 0.5, max: 6, default: 2 },
  glitch: { key: 'frequency', min: 1, max: 10, default: 4 },
  strobe: { key: 'frequency', min: 2, max: 15, default: 8 },
};
export const MAX_EFFECT_SECTIONS = 50;
export const MIN_SECTION_SECONDS = 1 / 30;
const MAX_SECTION_ID_LENGTH = 40;

export type EffectSection = {
  id: string;
  effect: EffectId;
  start: number;
  end: number;
  intensity: number;
  ease: boolean;
  params?: { speed?: number; frequency?: number };
};

export const isDynamicEffect = (id: string): id is DynamicEffectId =>
  (DYNAMIC_EFFECT_IDS as readonly string[]).includes(id);
const isLookEffect = (id: string): id is LookEffectId => (LOOK_EFFECT_IDS as readonly string[]).includes(id);
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const clamp = (value: unknown, min: number, max: number, fallback: number) =>
  isFiniteNumber(value) ? Math.min(max, Math.max(min, value)) : fallback;

export function normalizeEffectSections(value: unknown): EffectSection[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: EffectSection[] = [];
  for (const raw of value as unknown[]) {
    if (out.length >= MAX_EFFECT_SECTIONS) break;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const r = raw as Record<string, unknown>;
    const { id, effect } = r;
    if (typeof id !== 'string' || id.length === 0 || id.length > MAX_SECTION_ID_LENGTH || seen.has(id)) continue;
    if (typeof effect !== 'string') continue;
    const dynamic = isDynamicEffect(effect);
    if (!dynamic && !isLookEffect(effect)) continue;
    if (!isFiniteNumber(r.start) || !isFiniteNumber(r.end)) continue;
    const start = Math.max(0, r.start);
    const end = r.end;
    if (end - start < MIN_SECTION_SECONDS - 1e-9) continue;
    seen.add(id);
    const section: EffectSection = {
      id,
      effect,
      start,
      end,
      intensity: clamp(r.intensity, 0, 100, dynamic ? 80 : 100),
      ease: r.ease !== false,
    };
    const knob = EFFECT_SECTION_KNOBS[effect];
    if (knob) {
      const params = r.params && typeof r.params === 'object' ? (r.params as Record<string, unknown>) : {};
      section.params = { [knob.key]: clamp(params[knob.key], knob.min, knob.max, knob.default) };
    }
    out.push(section);
  }
  return out;
}

/** Unlocked (full-video) preview: the composition axis is source time. Map it
 *  to the clip output timeline; null outside every kept interval. */
export function mapSourceTimeToClipTime(
  sourceSec: number,
  intervals: ReadonlyArray<{ startTime: number; endTime: number }>,
): number | null {
  let offset = 0;
  for (const iv of intervals) {
    if (sourceSec >= iv.startTime && sourceSec < iv.endTime) return offset + (sourceSec - iv.startTime);
    offset += iv.endTime - iv.startTime;
  }
  return null;
}
